import { useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { classNames } from '@/utils/classNames';
import styles from './Input.module.css';

type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  label?: string;
  hint?: string;
  error?: string;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  inputClassName?: string;
};

export const Input = ({
  label,
  hint,
  error,
  leftIcon,
  rightIcon,
  inputClassName,
  required,
  id,
  className,
  ...rest
}: InputProps) => {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const hintId = `${inputId}-hint`;

  return (
    <div className={classNames(styles.root, className)}>
      {label && (
        <label htmlFor={inputId} className={styles.label}>
          {label}
          {required && (
            <span className={styles.asterisk} aria-hidden>
              *
            </span>
          )}
        </label>
      )}
      <div className={classNames(styles.inputWrapper, error && styles.hasError)}>
        {leftIcon && (
          <span className={styles.icon} aria-hidden>
            {leftIcon}
          </span>
        )}
        <input
          id={inputId}
          className={classNames(styles.input, inputClassName)}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={error || hint ? hintId : undefined}
          {...rest}
        />
        {rightIcon && (
          <span className={styles.icon} aria-hidden>
            {rightIcon}
          </span>
        )}
      </div>
      {(error || hint) && (
        <p
          id={hintId}
          className={classNames(styles.hint, error && styles.hintError)}
        >
          {error ?? hint}
        </p>
      )}
    </div>
  );
};
